import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { pathToSecretTemplateDetail } from '@/navigation/navigate';
import { secretService } from '@/services/secret-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretMutationKeys, secretQueryKeys } from './query-keys';

/**
 * Hook to create a secret template.
 * Returns a react-query mutation that accepts a template creation payload.
 * Invalidates the secret templates cache and navigates to the new template's detail page on success.
 */
export function useCreateSecretTemplate(tenantId?: UUID, tenantSlug?: string) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: secretMutationKeys.createSecretTemplate(tenantId),
    mutationFn: async (payload: {
      name: string;
      description?: string;
      schema: object;
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const template = await secretService.reconcileSecretTemplate(
        tenantId,
        payload
      );

      const projected = await pollForResource(() =>
        secretService.findSecretTemplate(tenantId, template.templateId!)
      );

      return projected ?? template;
    },
    onSuccess: (template) => {
      qc.invalidateQueries({
        queryKey: secretQueryKeys.templateList(tenantId),
        refetchType: 'all',
      });

      if (tenantSlug && template?.templateId) {
        navigate(pathToSecretTemplateDetail(tenantSlug, template.templateId));
      }
    },
  });
}
